/* eslint-disable */
import { useState, useEffect, useMemo } from "react";
import {
  yearToCropYearFull,
  yearToCropYearShort,
  OLAPTransformerResult,
  useParameter,
  useDictionaryItem,
  castUnit,
  GLOBAL_UNIT,
  Point,
} from "../../../shared";
import Highcharts, { SeriesOptionsType, SeriesPackedbubbleOptions } from "highcharts";
import { useChartOptions } from "../../../../charts/useChartOptions";
import { legendPipe, chartPipe, tooltipPipe, addXAxisPipe, addYAxisPipe } from "../../../../charts/pipes";
import { groupBy, orderBy, sumBy, sum } from "lodash";
import { shortCountries } from "../utils/shortCountries";
import { shortCommodities } from "../utils/shortCommodities";
import { dictionaryService } from "../../../dataFlow";
import { clone, merge, OLAPConsoleTable, uniq } from "../../../../../shared";

const CATEGORIES_AXIS = "commodities.default";
const SERIES_AXIS = "countries.default";

export function useTreemapChart(
  commoditiesCountries: Map<number, number[]> | null,
  worldData: OLAPTransformerResult | null,
  countriesData: OLAPTransformerResult | null,
  lastYearWorldData: OLAPTransformerResult | null,
  lastYearData: OLAPTransformerResult | null
): Highcharts.Options | undefined {
  const [state, setState] = useState<Highcharts.Options>();

  const [business] = [1];
  const [commoditiesGroupParam] = useParameter<number[]>("@commodities_groups");
  const [commoditiesGroupItem] = useDictionaryItem(dictionaryService, "slides-commodities-display", commoditiesGroupParam![0], {
    business_unit: Number(business),
    slide: 7,
  });

  const commoditiesGroupName = commoditiesGroupItem?.getValue<string>("comment") || "";

  let categories = useMemo(
    () =>
      worldData &&
      uniq(worldData.columns.map((x) => x.headers.find((h) => h.id === `${CATEGORIES_AXIS}.name`)?.name || "")).map((x) => x[9]),
    [worldData]
  );

  const options = useChartOptions(
    {
      plotOptions: {
        series: {
          dataLabels: {
            formatter: function () {
              return `${shortCountries(this.point["name"])}** ${Highcharts.numberFormat(this.point["change"], 1)} %`;
            },
          },
        },
      },
      colors: [
        "#197ca8", // DBLUE
        "rgba(15, 138, 192, 0.99)", // BLUE
        "rgba(27, 184, 194, 0.99)", // LBLUE
        "#40b66e", // GREEN
        "#D9D10C", // YELLOW
        "rgba(227, 129, 20, 0.99)", // OR
        "rgba(221, 92, 61, 0.99)", // RED
        "rgba(143, 143, 143, 0.99)", // GR
        "rgba(120, 79, 186, 0.99)", // PUR
      ],
      tooltip: {
        useHTML: true,
        formatter: function () {
          return this.point["commodity"] ? `<div>
          <p><b>${this.point["commodity"]}</b> - <b>${this.point["country"]}</b></p>
          <p>Share of country: ${Highcharts.numberFormat(this.point["share_of_country"], 0)} %</p>
          <p>Share of ${commoditiesGroupName}: ${this.point["share_of"] ? Highcharts.numberFormat(this.point["share_of"], 2) : "N/A"} %</p>
          ${`<p>World value: ${Highcharts.numberFormat(this.point["world_value"], 2)}</p>`}
          ${`<p>Change to the previous year: ${Highcharts.numberFormat(this.point["change"], 1)}%</p>`}
        </div>` : undefined;
        },
      },
    },
    [
      chartPipe("treemap"),
      legendPipe("top", {
        enabled: false,
      }),
      addXAxisPipe(categories),
      addXAxisPipe(null, {
        opposite: true,
        type: "category",
        min: 0,
        title: {
          text: "World value (MIO USD)",
        },
        labels: {
          align: "center",
          formatter: function () {
            return Highcharts.numberFormat(this.value, 0);
          },
        },
      }),
      addXAxisPipe(null, {
        type: "category",
        labels: {
          align: "center",
          formatter: function () {
            const stringVal = `${this.value}`;
            const short = shortCommodities(stringVal);
            return short === stringVal ? stringVal : `${short}*`;
          },
        },
        title: {
          text: `Crop value - % share of ${commoditiesGroupName}`,
        },
      }),
      addYAxisPipe({
        opposite: true,
        title: {
          text: "",
        },
      }),
      addYAxisPipe({
        labels: {
          formatter: function () {
            var label = this.axis.defaultLabelFormatter.call(this);
            if (/^[0-9]{4}$/.test(label)) {
              return Highcharts.numberFormat(this.value, 2);
            }
            return label;
          },
        },
        title: {
          text: "Crop value - % share of country",
        },
        tickInterval: 20,
        min: 0,
        max: 100,
      }),
    ]
  );

  useEffect(() => {
    if (worldData && countriesData && lastYearWorldData && lastYearData) {
      const worldWidth = new Map();
      const worldStake = new Map();
      const worldRows = worldData.rows;
      const worldPoints = worldData.rows.flatMap((x) => x.points);

      const worldValueMeasure =
        (worldPoints.map((p) => [p.attributes!["commodities.default.name"][9], p["world_value"]]) as [string, number][]) || [];
      const worldShareOfMeasure =
        (worldPoints.map((p) => [p.attributes!["commodities.default.name"][9], p["share_of"]]) as [string, number][]) || [];

      for (const point of worldValueMeasure) {
        point[1] = castUnit(point[1], GLOBAL_UNIT.CROPVALUE_MUSD, GLOBAL_UNIT.CROPVALUE_MUSD)!;
      }

      const avgWidth = sumBy(worldValueMeasure, ([, v]) => v) / (worldValueMeasure?.length || 1);
      const maxWidth = avgWidth * 2.5;
      const minWidth = avgWidth * 0.5;
      const setWidth = (value) => {
        if (value === 0 || value === null || value === undefined) return 0;
        if (value > maxWidth) return maxWidth - value / 1000;
        if (value < minWidth) return minWidth + value / 1000;
        return value;
      };

      for (const [col, val] of worldValueMeasure) {
        worldWidth.set(col, val);
      }

      const sort = <T>(items: T[] | null | undefined, getCategory: (item: T) => string) =>
        orderBy(
          items?.filter((item) => worldWidth.get(getCategory(item))),
          (item) => Number(worldWidth.get(getCategory(item)))
        ).reverse();

      categories = sort(categories, (x) => x);

      const shareOfPoints = sort(worldShareOfMeasure, (x) => x[0]);
      for (const [key, val] of shareOfPoints) {
        worldStake.set(key, val);
      }

      const topPoints = sort(worldValueMeasure, (x) => x[0]);

      const topSeries: SeriesOptionsType[] = [
        {
          type: "variwide",
          xAxis: 1,
          dataLabels: {
            enabled: false,
            align: "center",
            verticalAlign: "bottom",
          },
          showInLegend: false,
          name: worldRows[0]?.attributes[`${SERIES_AXIS}.name`],
          id: worldRows[0]?.attributes[`${SERIES_AXIS}.name`],
          data: topPoints.map(([, val]) => [`${val}`, 0, setWidth(val)]),
        },
      ];

      const bottonSeries: SeriesOptionsType[] = [
        {
          type: "variwide",
          xAxis: 2,
          dataLabels: {
            enabled: false,
            align: "center",
            verticalAlign: "bottom",
          },
          showInLegend: false,
          name: worldRows[0]?.attributes[`${SERIES_AXIS}.name`],
          id: worldRows[0]?.attributes[`${SERIES_AXIS}.name`],
          data: shareOfPoints.map(([key]) => [key, 0, setWidth(worldWidth.get(key))]),
        },
      ];
      const points = countriesData.rows
        .flatMap((x) => x.points)
        .filter((p) => Math.round((p as any)["share_of_country"]) >= (business === 2 ? 2 : 5));
      const pointsCountries = points.map((point) => (point.attributes || {})[`${SERIES_AXIS}.id`]);
      const lastYearPoints = lastYearData.rows
        .flatMap((x) => x.points)
        .filter((p) => pointsCountries.includes((p.attributes || {})[`${SERIES_AXIS}.id`]));

      const avgHeight = sumBy(worldValueMeasure, ([, v]) => v) / (worldValueMeasure?.length || 1);
      const maxHeight = avgHeight * 2.5;
      const minHeight = avgHeight * 0.5;
      const setHeight = (value) => {
        if (value === 0 || value === null || value === undefined) return 0;
        if (value > maxHeight) return maxHeight - value / 1000;
        if (value < minHeight) return minHeight + value / 1000;
        return value;
      };

      const groups = groupBy(points, (p) => p.attributes && p.attributes[`${CATEGORIES_AXIS}.id`]);
      const worldGroups = groupBy(worldPoints, (p) => p.attributes && p.attributes[`${CATEGORIES_AXIS}.id`]);
      if(Object.keys(groups).length < Object.keys(worldGroups).length) {
        Object.keys(worldGroups).filter(key => !Object.keys(groups).includes(key)).forEach(key => {
          groups[key] = worldGroups[key]
        })
      }
      const lastYearGroups = groupBy(lastYearPoints, (p) => p.attributes && p.attributes[`${CATEGORIES_AXIS}.id`]);
      for (const group of Array.from(Object.keys(groups))) {
        const groupPoints = groups[group];
        const lastYearGroupPoints = lastYearGroups[group];
        const worldValue = worldData.rows[0]?.points.find(
          (x) => Number(x.attributes![`${CATEGORIES_AXIS}.id`]) === Number(group)
        )?.world_value;
        const lastYearWorldValue = lastYearWorldData.rows[0]?.points.find(
          (x) => Number(x.attributes![`${CATEGORIES_AXIS}.id`]) === Number(group)
        )?.world_value;

        const filter = (p) => {
          const currentYearCountries = groupPoints.map((point) => Number(point?.attributes![`${SERIES_AXIS}.id`]));
          const countriesSet = commoditiesCountries?.get(Number(p?.attributes![`${CATEGORIES_AXIS}.id`]));
          const ignoreByCommoditySet = !(countriesSet && countriesSet.includes(Number(p?.attributes![`${SERIES_AXIS}.id`])));
          const ignoreByCurrentYearCountries = !currentYearCountries.includes(Number(p?.attributes![`${SERIES_AXIS}.id`]));
          return !(ignoreByCommoditySet || ignoreByCurrentYearCountries);
        };
        const restOfTheWorld = groupPoints?.filter(filter).reduce((sum, p) => sum - p["country_value"] / 1000, worldValue);
        const lastYearRestOfTheWorld = lastYearGroupPoints
          ?.filter(filter)
          .reduce((sum, p) => sum - p["country_value"] / 1000, lastYearWorldValue);
        if (restOfTheWorld && lastYearRestOfTheWorld) {
          const template = clone(groupPoints[0]);
          const currentYearShareOfCountry = ((restOfTheWorld || 0) / (worldValue || 0)) * 100;
          const restOfTheWorldPoint: Point = {
            column: template.column,
            change: (restOfTheWorld / lastYearRestOfTheWorld - 1) * 100,
            value: currentYearShareOfCountry,
            country_value: restOfTheWorld,
            share_of_country: currentYearShareOfCountry,
            world_value: template.world_value,
            share_of: template.share_of,
            attributes: template.attributes,
          };

          if (restOfTheWorldPoint.attributes) {
            restOfTheWorldPoint.attributes[`${SERIES_AXIS}.id`] = "";
            restOfTheWorldPoint.attributes[`${SERIES_AXIS}.name`] = business === 2 ? "APE other" : "Rest of the world";
          }
          points.push(restOfTheWorldPoint);
        }
      }

      const colors = [
        "#197ca8", // DBLUE
        "rgba(15, 138, 192, 0.99)", // BLUE
        "rgba(27, 184, 194, 0.99)", // LBLUE
        "#40b66e", // GREEN
        "#D9D10C", // YELLOW
        "rgba(227, 129, 20, 0.99)", // OR
        "rgba(221, 92, 61, 0.99)", // RED
        "rgba(143, 143, 143, 0.99)", // GR
        "rgba(120, 79, 186, 0.99)", // PUR
      ];

      const categoriesPoints = categories?.map((x) => ({
        id: x,
        name: x,
        value: setWidth(worldWidth.get(x)),
        levels: [
          {
            level: 1,
            layoutAlgorithm: "Strip",
          },
        ],
      }));

      const categoryPoints = points.reduce((res, p) => {
        const categoryKey = p?.attributes![`${CATEGORIES_AXIS}.name`][9];
        const group = res[categoryKey] || [];

        res[categoryKey] = [...group, p]
          .sort((a, b) => {
            return (a as any)["share_of_country"] - (b as any)["share_of_country"];
          })
          .slice(0, 5);
        return res;
      }, {});
      const columnSeries2 = {
        alternateStartingDirection: true,
        layoutAlgorithm: "stripes",
        type: "treemap",
        yAxis: 0,
        xAxis: 0,
        showInLegend: false,
        name: `Treemap`,
        data: sort(
          points
            .filter((p) => {
              const countriesSet = commoditiesCountries && commoditiesCountries.get(Number(p?.attributes![`${CATEGORIES_AXIS}.id`]));
              const hasOLAP = Boolean(p?.attributes![`${SERIES_AXIS}.id`]);
              const ignore = hasOLAP && !(countriesSet && countriesSet.includes(Number(p?.attributes![`${SERIES_AXIS}.id`])));
              return !ignore;
            })
            .map<any>((p) => ({
              type: "treemap",
              parent: p?.attributes![`${CATEGORIES_AXIS}.name`][9],
              commodity: p?.attributes![`${CATEGORIES_AXIS}.name`][9],
              country: p?.attributes![`${SERIES_AXIS}.name`],
              id: p?.attributes![`${SERIES_AXIS}.name`],
              name: p?.attributes![`${SERIES_AXIS}.name`],
              value: (p as any)["share_of_country"],
              y: (p as any)["share_of_country"],
              world_value: p["world_value"],
              share_of: p["share_of"],
              share_of_country: p["share_of_country"],
              width: worldWidth.get(p.attributes!["commodities.default.name"][9]),
              parentChange: worldStake.get(p.attributes!["commodities.default.name"][9]),
              change: (p as any)["change"],
              x: categories?.indexOf(p?.attributes![`${CATEGORIES_AXIS}.name`][9]),
              color: colors[categories?.indexOf(p?.attributes![`${CATEGORIES_AXIS}.name`][9]) || ""],
            })),
          (point) => point.parent
        ).concat(categoriesPoints),
      } as any;

      const result: Highcharts.Options = {
        ...options,
        series: [columnSeries2].concat(topSeries).concat(bottonSeries),
      };
      setState(result);
    } else {
      setState(undefined);
    }
  }, [worldData, countriesData, lastYearWorldData, lastYearData, JSON.stringify(options)]);
  return state;
}
